import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Product } from '@/data/products';
import { useShipping, ShippingTier } from './ShippingContext';
import { useAuthContext } from '@/lib/AuthProvider';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { getExpressShippingDiscountPercent, applyShippingDiscount } from '../services/shippingService';
import { useToast } from '@/hooks/use-toast';
import { 
  distributeDiscountAcrossItems, 
  LineItemForDiscount,
  getEffectivePrice as getEffectivePriceUtil,
  getLineItemTotal
} from '../utils/discountCalculations';

// Define cart item type
export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  onSalePrice?: number;
  onSale?: boolean;
  listingQuantity?: number;
  // Discount fields for line-item discount tracking
  discountAmount?: number;        // USD discount amount for this line item
  discountPercentage?: number;    // actual percentage applied to this item
  discountedTotal?: number;       // line total after discount in USD
  rushdTreat?: boolean;           // Flag for auto-added Rush'd treats
}

export type ShippingOption = string; // Changed to string to support dynamic tier IDs

// Applied discount interface
export interface AppliedDiscount {
  discount: {
    id: string;
    code: string;
    description: string;
    type: 'percentage' | 'fixed' | 'free_shipping';
    value: number;
    minOrderAmount: number;
    maxUses: number;
    currentUses: number;
    status: 'active' | 'inactive' | 'expired';
    startDate: Date;
    endDate: Date;
    applicableProducts: string[];
    createdAt: Date;
    updatedAt: Date;
  };
  discountAmount: number;
  // Line item discount tracking
  lineItemDiscounts?: {
    [itemId: string]: {
      discountAmount: number;     // USD discount amount for this line item
      discountPercentage: number; // actual percentage applied to this item
    }
  };
}

// Firebase cart data structure
interface CartData {
  cartItems: CartItem[];
  shippingOption: ShippingOption;
  priorityProcessing: boolean;
  appliedDiscount: AppliedDiscount | null;
  updatedAt: number;
}

// Define context type
interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity: number) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
  shippingOption: ShippingOption;
  setShippingOption: (option: ShippingOption) => void;
  getShippingCost: () => Promise<number>;
  getShippingCostSync: () => number; // For compatibility with existing synchronous calls
  priorityProcessing: boolean;
  setPriorityProcessing: (value: boolean) => void;
  getPriorityProcessingFee: () => number;
  availableShippingTiers: ShippingTier[];
  loadingShippingTiers: boolean;
  appliedDiscount: AppliedDiscount | null;
  setAppliedDiscount: (discount: AppliedDiscount | null) => void;
  getFinalShippingCost: () => number; // Get shipping cost after discounts
  getFinalTotal: () => number; // Get total after all discounts
  getDiscountAmount: () => number; // Get discount amount for percentage/fixed discounts
  discountLoaded: boolean; // Whether express shipping discount has been loaded
  applyDiscountToCartItems: (discount: AppliedDiscount) => CartItem[]; // Apply discount proportionally across cart items
  getCartTotalWithDiscounts: () => number; // Get cart total with line-item discounts applied
  setApplyingDiscount: (applying: boolean) => void; // Control flag to prevent discount invalidation during application
  syncRushdTreatItems: (
    treats: { productId: string; productName: string; productImage?: string | null; quantity: number }[],
    isActiveMember: boolean
  ) => Promise<void>;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Cart provider props
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Use shipping context and auth context
  const { getActiveShippingTiers, getShippingTierById, calculateShippingCost, loading: loadingShippingTiers, dhlExpressRate } = useShipping();
  const { user } = useAuthContext();
  const { toast } = useToast();
  
  // Start with empty state, will be populated from Firebase/localStorage
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [shippingOption, setShippingOption] = useState<ShippingOption>("standard");
  const [priorityProcessing, setPriorityProcessing] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);
  // Flag to track if initial load has happened
  const [initialized, setInitialized] = useState(false);
  
  // Track last user ID to detect user switching
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  
  // Track when we're applying a discount to prevent invalidation during application
  const [applyingDiscount, setApplyingDiscount] = useState<boolean>(false);
  
  // Cache express shipping discount percentage for sync operations
  const [cachedDiscountPercent, setCachedDiscountPercent] = useState<number>(0);
  const [discountLoaded, setDiscountLoaded] = useState<boolean>(false);
  
  // Get available shipping tiers from context
  const availableShippingTiers = getActiveShippingTiers();
  
  // Helper function to check if current discount affects subtotal (percentage or fixed amount)
  const shouldInvalidateDiscount = (): boolean => {
    return appliedDiscount && (appliedDiscount.discount.type === 'percentage' || appliedDiscount.discount.type === 'fixed');
  };
  
  // Helper function to recalculate discount distribution when cart changes
  const recalculateDiscountIfNeeded = () => {
    // Don't recalculate discount if we're currently applying one
    if (applyingDiscount) {
      return;
    }
    
    if (shouldInvalidateDiscount()) {
      // Instead of removing the discount, recalculate it for the updated cart
      const currentDiscount = appliedDiscount!;
      
      // Check if cart is now empty
      if (cartItems.length === 0) {
        setAppliedDiscount(null);
        localStorage.removeItem('jamaicaAppliedDiscount');
        if (user) {
          saveCartToFirebase({ appliedDiscount: null });
        }
        return;
      }
      
      // For fixed discounts, check if cart total is now less than discount amount
      if (currentDiscount.discount.type === 'fixed') {
        const newCartTotal = cartItems.reduce((total, item) => {
          const price = (item.onSale && item.onSalePrice && item.onSalePrice < item.price) 
            ? item.onSalePrice 
            : item.price;
          return total + (price * item.quantity);
        }, 0);
        
        // Cap discount at new cart total
        const cappedDiscountAmount = Math.min(currentDiscount.discount.value, newCartTotal);
        currentDiscount.discountAmount = cappedDiscountAmount;
      }
      
      // Recalculate line-item discounts for the updated cart
      const itemsWithRecalculatedDiscounts = applyDiscountToCartItems(currentDiscount);
      
      // Extract updated line-item discount data
      const updatedLineItemDiscounts: { [itemId: string]: { discountAmount: number; discountPercentage: number; } } = {};
      
      itemsWithRecalculatedDiscounts.forEach(item => {
        if (item.discountAmount !== undefined && item.discountPercentage !== undefined) {
          updatedLineItemDiscounts[item.id] = {
            discountAmount: item.discountAmount,
            discountPercentage: item.discountPercentage
          };
        }
      });

      // Update the applied discount with recalculated line-item discounts
      const updatedDiscount: AppliedDiscount = {
        ...currentDiscount,
        lineItemDiscounts: updatedLineItemDiscounts
      };
      
      setAppliedDiscount(updatedDiscount);
      
      // Update storage
      localStorage.setItem('jamaicaAppliedDiscount', JSON.stringify(updatedDiscount));
      if (user) {
        saveCartToFirebase({ appliedDiscount: updatedDiscount });
      }
    }
  };

  const syncRushdTreatItems = useCallback(
    async (
      treats: { productId: string; productName: string; productImage?: string | null; quantity: number }[],
      isActiveMember: boolean
    ): Promise<void> => {
      setCartItems(prev => {
        if (!isActiveMember) {
          return prev.filter(item => !item.rushdTreat);
        }

        let updated = prev.filter(
          item => !item.rushdTreat || treats.some(t => t.productId === item.id)
        );

        treats.forEach(treat => {
          const existingIndex = updated.findIndex(
            item => item.id === treat.productId && item.rushdTreat
          );
          if (existingIndex >= 0) {
            updated[existingIndex] = {
              ...updated[existingIndex],
              name: treat.productName,
              image: treat.productImage || updated[existingIndex].image,
              quantity: treat.quantity,
              price: 0,
              rushdTreat: true,
            };
          } else {
            updated.push({
              id: treat.productId,
              name: treat.productName,
              image: treat.productImage || '/placeholder.png',
              price: 0,
              quantity: treat.quantity,
              rushdTreat: true,
            });
          }
        });

        return updated;
      });
    },
    []
  );
  
  // Helper functions for cart persistence
  const getCartDocRef = () => user ? doc(db, 'carts', user.uid) : null;
  
  const loadFromStorage = (key: string, fallback: any = null) => {
    try {
      const saved = localStorage.getItem(key);
      if (!saved) return fallback;
      
      // Handle string values that don't need JSON parsing
      if (key === 'jamaicaShippingOption' && !saved.startsWith('{') && !saved.startsWith('[')) {
        // Remove quotes if present and return as string
        return saved.replace(/"/g, '');
      }
      
      return JSON.parse(saved);
    } catch (error) {
      localStorage.removeItem(key);
      return fallback;
    }
  };
  
  const saveToStorage = (key: string, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  };
  
  const saveCartToFirebase = async (data: Partial<CartData>) => {
    const docRef = getCartDocRef();
    if (!docRef) return;
    
    try {
      await setDoc(docRef, { ...data, updatedAt: Date.now() }, { merge: true });
    } catch (error) {
      // Silent error handling
    }
  };
  
  // Clear cart when user switches (different from logout which preserves localStorage)
  useEffect(() => {
    const currentUserId = user?.uid || null;
    
    // Only reset if switching between two different authenticated users
    // Don't reset when going from null to user (sign in) or user to null (sign out)
    if (lastUserId && currentUserId && currentUserId !== lastUserId) {
      setCartItems([]);
      setShippingOption("standard");
      setPriorityProcessing(false);
      setAppliedDiscount(null);
      setInitialized(false); // Force reload for new user
    }
    
    // When user signs out, reset initialized to force reload when they sign back in
    if (lastUserId && !currentUserId) {
      setInitialized(false);
    }
    
    setLastUserId(currentUserId);
  }, [user, lastUserId]);

  // Load cart data from Firebase (authenticated) or localStorage (non-authenticated)
  useEffect(() => {
    if (initialized) return;
    
    
    // Don't load cart while auth is still loading (user === undefined)
    if (user === undefined) {
      return;
    }
    
    const loadCartData = async () => {
      if (user) {
        // Load from Firebase for authenticated users
        const docRef = getCartDocRef();
        if (docRef) {
          try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              // Firebase cart exists - use it and overwrite any localStorage data
              const data = docSnap.data() as CartData;
              setCartItems(data.cartItems || []);
              setShippingOption(data.shippingOption || "standard");
              setPriorityProcessing(data.priorityProcessing || false);
              setAppliedDiscount(data.appliedDiscount || null);
            } else {
              // No Firebase cart exists - check if user has localStorage items to migrate
              const localCartItems = loadFromStorage('jamaicaCart', []);
              const localShippingOption = loadFromStorage('jamaicaShippingOption', "standard");
              const localPriorityProcessing = loadFromStorage('jamaicaPriorityProcessing', false);
              const localAppliedDiscount = loadFromStorage('jamaicaAppliedDiscount', null);
              
              if (localCartItems.length > 0) {
                // Migrate localStorage cart to Firebase
                setCartItems(localCartItems);
                setShippingOption(localShippingOption);
                setPriorityProcessing(localPriorityProcessing);
                setAppliedDiscount(localAppliedDiscount);
                
                // Save to Firebase immediately
                await saveCartToFirebase({
                  cartItems: localCartItems,
                  shippingOption: localShippingOption,
                  priorityProcessing: localPriorityProcessing,
                  appliedDiscount: localAppliedDiscount
                });
              } else {
                // No items anywhere - start fresh
                setCartItems([]);
                setShippingOption("standard");
                setPriorityProcessing(false);
                setAppliedDiscount(null);
              }
            }
          } catch (error) {
            // Fallback to localStorage on Firebase error
            setCartItems(loadFromStorage('jamaicaCart', []));
            setShippingOption(loadFromStorage('jamaicaShippingOption', "standard"));
            setPriorityProcessing(loadFromStorage('jamaicaPriorityProcessing', false));
            setAppliedDiscount(loadFromStorage('jamaicaAppliedDiscount', null));
          }
        }
      } else {
        // Load from localStorage for non-authenticated users
        setCartItems(loadFromStorage('jamaicaCart', []));
        setShippingOption(loadFromStorage('jamaicaShippingOption', "standard"));
        setPriorityProcessing(loadFromStorage('jamaicaPriorityProcessing', false));
        setAppliedDiscount(loadFromStorage('jamaicaAppliedDiscount', null));
      }
      
      setInitialized(true);
    };
    
    loadCartData();
  }, [user, initialized]);
  
  // Set default shipping option when tiers are loaded
  useEffect(() => {
    if (!loadingShippingTiers && availableShippingTiers.length > 0 && initialized) {
      // Check if current shipping option exists in available tiers
      const currentOptionExists = availableShippingTiers.some(tier => tier.id === shippingOption);
      
      if (!currentOptionExists) {
        // Current option doesn't exist, set to standard or first available
        const standardTier = availableShippingTiers.find(tier => tier.id === 'standard');
        const defaultOption = standardTier ? standardTier.id : availableShippingTiers[0].id;
        
        setShippingOption(defaultOption);
      }
    }
  }, [loadingShippingTiers, availableShippingTiers, initialized, shippingOption]);
  
  // Recalculate discounts when cart items change
  useEffect(() => {
    // Don't recalculate during initial load
    if (!initialized) return;
    
    // Recalculate discount distribution for the updated cart
    recalculateDiscountIfNeeded();
  }, [cartItems, initialized]);
  
  // Save cart to localStorage and Firebase whenever it changes, but only after initialization
  useEffect(() => {
    // Don't save during initial load
    if (!initialized) return;
    
    // Always save to localStorage as fallback
    localStorage.setItem('jamaicaCart', JSON.stringify(cartItems));
    
    // Also save to Firebase for authenticated users
    if (user) {
      saveCartToFirebase({ cartItems });
    }
  }, [cartItems, initialized, user]);
  
  // Save shipping option to localStorage and Firebase when it changes
  useEffect(() => {
    if (!initialized) return;
    
    // Always save to localStorage as fallback
    localStorage.setItem('jamaicaShippingOption', shippingOption);
    
    // Also save to Firebase for authenticated users
    if (user) {
      saveCartToFirebase({ shippingOption });
    }
  }, [shippingOption, initialized, user]);
  
  // Save priority processing to localStorage and Firebase when it changes
  useEffect(() => {
    if (!initialized) return;
    
    // Always save to localStorage as fallback
    localStorage.setItem('jamaicaPriorityProcessing', priorityProcessing.toString());
    
    // Also save to Firebase for authenticated users
    if (user) {
      saveCartToFirebase({ priorityProcessing });
    }
  }, [priorityProcessing, initialized, user]);
  
  // Save applied discount to localStorage and Firebase when it changes
  useEffect(() => {
    if (!initialized) return;
    
    // Always save to localStorage as fallback
    if (appliedDiscount) {
      localStorage.setItem('jamaicaAppliedDiscount', JSON.stringify(appliedDiscount));
    } else {
      localStorage.removeItem('jamaicaAppliedDiscount');
    }
    
    // Also save to Firebase for authenticated users
    if (user) {
      saveCartToFirebase({ appliedDiscount });
    }
  }, [appliedDiscount, initialized, user]);

  // Load and cache express shipping discount percentage
  useEffect(() => {
    const loadDiscountPercent = async () => {
      try {
        const discountPercent = await getExpressShippingDiscountPercent();
        setCachedDiscountPercent(discountPercent);
        setDiscountLoaded(true);
      } catch (error) {
        setCachedDiscountPercent(0); // Default to no discount on error
        setDiscountLoaded(true); // Still mark as loaded to prevent hanging
      }
    };

    loadDiscountPercent();
    
    // Refresh discount percentage every 5 minutes
    const intervalId = setInterval(loadDiscountPercent, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);
  
  // Add item to cart
  const addToCart = (product: Product, quantity: number) => {
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(item => item.id === product.id);
      
      if (existingItemIndex >= 0) {
        // For free items (price $0), don't increase quantity - keep it at 1
        if (product.price === 0) {
          return prevItems; // Don't modify cart if free item already exists
        }
        
        // Update quantity if item exists and is not free
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        };
        return updatedItems;
      } else {
        // Add new item if it doesn't exist
        const newItem: CartItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image || product.images[0],
          quantity: quantity,
          onSalePrice: product.onSalePrice,
          onSale: product.onSale,
          listingQuantity: product.listingQuantity
        };
        return [...prevItems, newItem];
      }
    });
  };
  
  // Remove item from cart
  const removeFromCart = (id: string) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== id));
  };
  
  // Update item quantity
  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    
    setCartItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          // For free items (price $0), always keep quantity at 1
          if (item.price === 0) {
            return { ...item, quantity: 1 };
          }
          return { ...item, quantity };
        }
        return item;
      })
    );
  };
  
  // Clear the entire cart
  const clearCart = () => {
    setCartItems([]);
    setAppliedDiscount(null);
    setShippingOption("standard"); // Reset to standard shipping
    setPriorityProcessing(false);
    
    // Immediately clear localStorage to prevent restoration
    localStorage.removeItem('jamaicaCart');
    localStorage.removeItem('jamaicaAppliedDiscount');
    localStorage.removeItem('jamaicaShippingOption');
    localStorage.removeItem('jamaicaPriorityProcessing');
    
    // Also clear from Firebase for authenticated users
    if (user) {
      saveCartToFirebase({ 
        cartItems: [], 
        appliedDiscount: null,
        shippingOption: "standard",
        priorityProcessing: false
      });
    }
  };
  
  // Calculate cart total
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // Use sale price only if item is currently on sale
      const price = (item.onSale && item.onSalePrice && item.onSalePrice < item.price) 
        ? item.onSalePrice 
        : item.price;
      return total + (price * item.quantity);
    }, 0);
  };
  
  // Get total number of items in cart
  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };
  
  // Calculate shipping cost - async version using Firebase config
  const getShippingCost = async (): Promise<number> => {
    const subtotal = getCartTotal();
    const tier = getShippingTierById(shippingOption);
    if (!tier) return 0;
    
    // Check if order qualifies for free shipping first
    if (tier.freeShippingThreshold && tier.freeShippingThreshold > 0 && subtotal >= tier.freeShippingThreshold) {
      return 0;
    }
    
    // Use DHL rate for express shipping if available (already discounted)
    if (tier.id === 'express' && dhlExpressRate !== null) {
      return dhlExpressRate;
    }
    
    // Calculate fallback shipping cost
    const baseCost = calculateShippingCost(tier, subtotal);
    
    // Apply express discount to fallback rates for express tier
    if (tier.id === 'express') {
      return applyShippingDiscount(baseCost, cachedDiscountPercent);
    }
    
    // Return fallback cost without discount for non-express tiers
    return baseCost;
  };
  
  // Calculate shipping cost - synchronous fallback for compatibility
  const getShippingCostSync = (): number => {
    const subtotal = getCartTotal();
    const tier = getShippingTierById(shippingOption);
    if (!tier) return 0;
    
    // Check if order qualifies for free shipping first
    if (tier.freeShippingThreshold && tier.freeShippingThreshold > 0 && subtotal >= tier.freeShippingThreshold) {
      return 0;
    }
    
    // Use DHL rate for express shipping if available (already discounted)
    if (tier.id === 'express' && dhlExpressRate !== null) {
      return dhlExpressRate;
    }
    
    // Calculate fallback shipping cost
    const baseCost = calculateShippingCost(tier, subtotal);
    
    // Apply express discount to fallback rates for express tier
    if (tier.id === 'express') {
      return applyShippingDiscount(baseCost, cachedDiscountPercent);
    }
    
    // Return fallback cost without discount for non-express tiers
    return baseCost;
  };
  
  // Get final shipping cost after applying discounts
  const getFinalShippingCost = (): number => {
    const baseShippingCost = getShippingCostSync();
    const isFreeShippingDiscount = appliedDiscount?.discount.type === 'free_shipping';
    return isFreeShippingDiscount ? 0 : baseShippingCost;
  };
  
  // Get discount amount for percentage/fixed discounts (not free shipping)
  const getDiscountAmount = (): number => {
    if (!appliedDiscount || appliedDiscount.discount.type === 'free_shipping') return 0;
    return appliedDiscount.discountAmount || 0;
  };
  
  // Calculate priority processing fee
  const getPriorityProcessingFee = () => {
    return priorityProcessing ? 3.0 : 0;
  };

  // Apply discount proportionally across cart items
  const applyDiscountToCartItems = (discount: AppliedDiscount): CartItem[] => {
    if (!discount || discount.discount.type === 'free_shipping') {
      // For free shipping or no discount, clear any existing discounts from items
      return cartItems.map(item => ({
        ...item,
        discountAmount: undefined,
        discountPercentage: undefined,
        discountedTotal: undefined
      }));
    }

    // Convert cart items to the format expected by discount calculations
    const itemsForDiscount: LineItemForDiscount[] = cartItems.map(item => ({
      id: item.id,
      quantity: item.quantity,
      price: item.price,
      onSalePrice: item.onSalePrice,
      onSale: item.onSale
    }));

    // Distribute the discount across items
    const distribution = distributeDiscountAcrossItems(
      itemsForDiscount,
      discount.discount.type as 'percentage' | 'fixed',
      discount.discount.type === 'percentage' ? discount.discount.value : discount.discountAmount
    );

    // Apply the distributed discounts to cart items
    return cartItems.map(item => {
      const itemDiscount = distribution.lineItemDiscounts.find(d => d.itemId === item.id);
      if (itemDiscount) {
        return {
          ...item,
          discountAmount: itemDiscount.discountAmount,
          discountPercentage: itemDiscount.discountPercentage,
          discountedTotal: itemDiscount.discountedTotal
        };
      }
      return {
        ...item,
        discountAmount: undefined,
        discountPercentage: undefined,
        discountedTotal: undefined
      };
    });
  };

  // Get cart total with line-item discounts applied
  const getCartTotalWithDiscounts = (): number => {
    if (!appliedDiscount || appliedDiscount.discount.type === 'free_shipping') {
      return getCartTotal(); // Use original calculation for free shipping or no discount
    }

    // Use precise proportional distribution method for mathematical consistency
    // This ensures the total matches exactly what users see in line item display
    const itemsWithDiscounts = applyDiscountToCartItems(appliedDiscount);
    
    return cartItems.reduce((total, item) => {
      // Calculate original line item total in USD
      const unitPriceUsd = (item.onSale && item.onSalePrice && item.onSalePrice < item.price) 
        ? item.onSalePrice 
        : item.price;
      const originalLineTotal = unitPriceUsd * item.quantity;
      
      // Check if this item has a discount applied
      const discountedItem = itemsWithDiscounts.find(discountItem => discountItem.id === item.id);
      const hasDiscount = discountedItem && discountedItem.discountedTotal !== undefined;
      
      if (hasDiscount) {
        // Use the precise discounted total from proportional distribution
        return total + discountedItem.discountedTotal!;
      } else {
        // No discount, use original total
        return total + originalLineTotal;
      }
    }, 0);
  };
  
  // Get final total after all discounts
  const getFinalTotal = (): number => {
    const subtotal = getCartTotalWithDiscounts(); // Use new method that considers line-item discounts
    const shipping = getFinalShippingCost();
    const priorityFee = getPriorityProcessingFee();
    
    return Math.max(0, subtotal + shipping + priorityFee);
  };
  
  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    shippingOption,
    setShippingOption,
    getShippingCost,
    getShippingCostSync,
    priorityProcessing,
    setPriorityProcessing,
    getPriorityProcessingFee,
    availableShippingTiers,
    loadingShippingTiers,
    appliedDiscount,
    setAppliedDiscount,
    getFinalShippingCost,
    getFinalTotal,
    getDiscountAmount,
    discountLoaded,
    applyDiscountToCartItems,
    getCartTotalWithDiscounts,
    setApplyingDiscount,
    syncRushdTreatItems,
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 
